/**
 * Iconurile pe care le poate purta un tip de pachet (internet, telefonie, colocare…).
 *
 * **Listă închisă, nu string liber**, din același motiv ca la firme
 * (`company-icons.ts`): valoarea ajunge într-un `Icons[valoare]` în frontend, iar un nume
 * greșit ar randa `undefined` într-un `<Component />` — adică un ecran alb, nu un icon
 * lipsă. Aici e validată la scriere, deci ce e în bază se poate reda întotdeauna.
 *
 * Stă în `shared` pentru că lista trebuie să fie **aceeași în cele două capete**:
 * backendul validează după ea, `apps/crm` mapează după ea la componente `lucide-react`.
 * Două copii ar diverge, iar divergența s-ar vedea abia ca icon dispărut la un tip anume.
 *
 * ## De ce nu e aceeași listă cu a firmelor
 *
 * Se suprapun mult (`Wifi`, `Server`, `Phone`), dar **răspund la întrebări diferite**: acolo
 * se alege cum arată o firmă a grupului, aici ce fel de serviciu e. De aceea și implicitul
 * diferă — `Building2` pentru o firmă, un pachet neutru pentru un serviciu — iar o listă
 * comună ar fi trebuit oricum să se rupă la primul icon care are sens doar într-un loc.
 *
 * Numele sunt cele din `lucide-react`, neschimbate — o redenumire aici ar cere migrare de
 * date pentru nimic. Se extinde prin adăugarea unei linii, iar valorile vechi rămân valide.
 *
 * ⚠️ **Ordinea contează**: e ordinea în care apar în grila de alegere, iar comentariile de
 * familie de mai jos sunt chiar împărțirea pe grupuri din selector. O linie adăugată în
 * mijlocul unei familii ajunge acolo unde o pui. Etichetele grupurilor stau în `apps/crm`
 * (`package-type-icons.tsx`), ca și numele românești ale iconurilor — `shared` e importat și
 * de backend, care n-are ce face cu texte de interfață.
 *
 * ⚠️ **Nu se scot nume din listă.** Un nume scos rămâne pe rândurile care l-au ales și cade
 * pe implicit — adică cineva pierde tăcut iconul pe care-l pusese.
 */
export const packageTypeIconKeys = [
  // Neutru — implicitul. Primul, ca să fie primul și în grilă.
  'Package',
  // Internet și rețea
  'Wifi',
  'Router',
  'Network',
  'Cable',
  'Waypoints',
  'Antenna',
  'RadioTower',
  'SatelliteDish',
  'Signal',
  'Globe',
  'Radio',
  'Rss',
  // Telefonie și comunicații
  'Phone',
  'PhoneCall',
  'Smartphone',
  'Headset',
  'MessageSquare',
  'Mail',
  // Datacenter și găzduire
  'Server',
  'Cpu',
  'HardDrive',
  'Database',
  'Cloud',
  'Container',
  'Boxes',
  'Building2',
  'Warehouse',
  // TV și video
  'Tv',
  'MonitorPlay',
  'Video',
  'Cctv',
  // Securitate
  'ShieldCheck',
  'Lock',
  'KeyRound',
  'Fingerprint',
  // Servicii administrate și suport
  'Wrench',
  'Cog',
  'LifeBuoy',
  'Users',
  'Briefcase',
  // Monitorizare
  'Activity',
  'Gauge',
  'BellRing',
  // Alimentare și altele
  'Zap',
  'PlugZap',
  'MapPin',
  'Printer',
] as const;

export type PackageTypeIconKey = (typeof packageTypeIconKeys)[number];

/**
 * Ce se folosește când tipul n-are icon ales.
 *
 * Coloana e `NULL`-abilă (tipurile existente n-au ales nimic), dar interfața **nu are stare
 * de „fără icon"**: ar fi un gol în listă exact acolo unde ochiul caută reperul. Un pachet
 * neutru, nu un semn de întrebare — tipul există și e valid, doar n-a fost împodobit.
 */
export const DEFAULT_PACKAGE_TYPE_ICON: PackageTypeIconKey = 'Package';

/** Lungimea coloanei `crm_package_types.icon`. Cel mai lung nume de azi are 13 caractere. */
export const PACKAGE_TYPE_ICON_MAX_LENGTH = 40;

const packageTypeIconKeySet: ReadonlySet<string> = new Set<string>(packageTypeIconKeys);

export function isPackageTypeIconKey(value: string): value is PackageTypeIconKey {
  return packageTypeIconKeySet.has(value);
}

/**
 * Valoarea din bază → un icon care sigur se poate reda.
 *
 * Se **ignoră** ce nu e cunoscut, nu se aruncă: un nume scos din listă la un refactor rămâne
 * pe rândurile vechi, iar o eroare acolo ar face ca fișa unui client să nu se mai poată
 * deschide din cauza unui icon. Aceeași alegere ca la firme și la `filterKnownPermissionKeys`.
 */
export function normalizePackageTypeIcon(value: string | null | undefined): PackageTypeIconKey {
  return value && isPackageTypeIconKey(value) ? value : DEFAULT_PACKAGE_TYPE_ICON;
}
